import { useEffect, useState } from "react";

import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { useTourState } from "@/hooks/useTourState";
import { areSuggestionsDisabled, SUGGESTIONS_PREF_EVENT } from "@/lib/suggestionPrefs";

/**
 * Mounted once near the app root, passing through the current route's
 * conceptId so per-concept Card dismissal can be evaluated. Reads the
 * existing stores + localStorage; writes derived canShow + activeSurface
 * claims into useOnboardingStore.
 */
export function useOnboardingController(conceptId: string | undefined): void {
  const catalogOpen = useGuidesStore((s) => s.catalogOpen);
  const openWalkthroughId = useGuidesStore((s) => s.openWalkthroughId);
  const tour = useTourState();
  const tourShouldShow = tour.shouldShow;
  // Subscribe to activeSurface — it drives both canShow.suggestion (hide
  // the Card behind any modal) and canShow.announcement (suppress
  // announcements while the tour is active). Reading via selector keeps
  // canShow reactive when claim/release fires from any source.
  const activeSurface = useOnboardingStore((s) => s.activeSurface);

  // Workflow nudges are decoupled from guide phase (experts see them too);
  // the only permanent opt-out is the dedicated global pref. Mirror it into
  // state so canShow recomputes when the user toggles it from the drawer.
  const [suggestionsDisabled, setSuggestionsDisabled] = useState(areSuggestionsDisabled);
  useEffect(() => {
    const refresh = () => setSuggestionsDisabled(areSuggestionsDisabled());
    window.addEventListener(SUGGESTIONS_PREF_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SUGGESTIONS_PREF_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    useOnboardingStore.setState({
      canShow: {
        suggestion: !!conceptId && activeSurface === null && !suggestionsDisabled,
        tour: tourShouldShow,
        announcement: activeSurface !== "tour",
      },
    });
  }, [conceptId, activeSurface, tourShouldShow, suggestionsDisabled]);

  // Translate guides-store open state into claim/release.
  useEffect(() => {
    const store = useOnboardingStore.getState();
    if (openWalkthroughId) store.claim("walkthrough");
    else store.release("walkthrough");
  }, [openWalkthroughId]);

  useEffect(() => {
    const store = useOnboardingStore.getState();
    if (catalogOpen) store.claim("catalog");
    else store.release("catalog");
  }, [catalogOpen]);
}
